import { useCallback, useEffect, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { formatDateRelative } from "simplydo/core/dates";
import { OpenAPI } from "simplydo/interfaces";
import { useThrottle } from "simplydo/react/hooks";
import { styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";
import {
  Button,
  Container,
  Icon,
  Label,
  Loader,
  Pagination,
  Table,
} from "simplydo/react/ui";
import { Input } from "simplydo/react/ui";

import actions from "actions";
import api from "api";
import { useAppDispatch, useAppSelector } from "store";
import util from "utils/utils";

import { UserChip } from "components/lib/Chips";
import { EmptyBox } from "components/lib/UI";

const BannerContainer = styled.div`
  display: flex;
  flex-direction: ${({ theme }) => (theme.sizes.isComputer ? "row" : "column")};
  justify-content: ${({ theme }) =>
    theme.sizes.isComputer ? "space-between" : "centre"};
  align-items: ${({ theme }) =>
    theme.sizes.isComputer ? "flex-end" : "centre"};
  h1 {
    margin: 0 0 10px 0;
    color: #3b3b3b;
  }
  .ui.input {
    width: 360px;
  }
`;

const NavigationArea = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  span {
    display: block;
    font-weight: 700;
  }
`;

const MessageRow = styled(Table.Row)`
  cursor: pointer;
  &:hover {
    background-color: #e9ebee;
  }
`;

const MessageCentre = () => {
  const user = useAppSelector((state) => state.user);
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  type MessageThread =
    OpenAPI.GET<`/messages/threads`>["response"]["messageThreads"][number];
  const onMessageThreadSelected = useCallback(
    (thread: MessageThread, isPhone = false) =>
      dispatch(actions.messages.selectThread(thread, isPhone)),
    [dispatch],
  );

  const [queryState, setQueryState] = useState({ page: 1, query: "" });
  const [messageState, setMessageState] = useState<
    OpenAPI.GET<`/messages/threads`>["response"]
  >({
    messageThreads: [],
    nextPageAvailable: false,
    previousPageAvailable: false,
    total: 0,
    newMessageCount: 0,
    page: 1,
  });
  const [messagesLoading, setMessagesLoading] = useState(false);
  const { page, query } = queryState;

  const fetchThreads = useThrottle(
    (nextPage, nextQuery) => {
      api.messages.getThreads(
        { page: nextPage, query: nextQuery },
        (data) => {
          setMessageState(data);
          setMessagesLoading(false);
        },
        (err) => {
          toast.error(err.message);
          setMessagesLoading(false);
        },
      );
    },
    400,
    [],
  );

  const getThreads = useCallback(
    (nextPage: number, nextQuery: string) => {
      setMessagesLoading(true);
      setQueryState({ page: nextPage, query: nextQuery });
      fetchThreads(nextPage, nextQuery);
    },
    [fetchThreads],
  );

  useEffect(() => {
    getThreads(1, "");
  }, [getThreads]);

  const { messageThreads, total } = messageState;

  return (
    <>
      <div style={{ marginTop: 20 }}>
        <Container>
          <BannerContainer>
            <div>
              <h1>{t("messages.title")}</h1>
              <Input
                style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                loading={messagesLoading}
                placeholder={t("messages.searchPlaceholder")}
                onChange={(e) => getThreads(1, e.target.value)}
                value={query}
                icon="search"
              />
            </div>
            <Button as={Link} to="/preferences/notifications">
              <Icon name="settings" />
              {t("messages.updateSettings")}
            </Button>
          </BannerContainer>
        </Container>
      </div>
      <Container style={{ marginTop: 20 }}>
        {messageThreads && messageThreads.length > 0 ?
          <Table unstackable>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Chat</Table.HeaderCell>
                <Table.HeaderCell>With</Table.HeaderCell>
                {!theme.sizes.isMobile && (
                  <Table.HeaderCell>
                    {t("messages.table.lastMessage")}
                  </Table.HeaderCell>
                )}
                <Table.HeaderCell>
                  {t("messages.table.received")}
                </Table.HeaderCell>
                <Table.HeaderCell />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {messageThreads.map((thread) => {
                const { lastMessageAt, lastMessageSnippet = "" } = thread;

                const otherUsers =
                  thread.participantUsers?.filter((u) => u._id !== user?._id) ??
                  [];

                return (
                  <MessageRow
                    key={thread._id}
                    onClick={() =>
                      onMessageThreadSelected(thread, !theme.sizes.isComputer)
                    }
                  >
                    <Table.Cell
                      collapsing
                      style={{
                        maxWidth: 400,
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                      }}
                    >
                      {thread.seenBy &&
                        user?._id &&
                        thread.seenBy.indexOf(user._id) === -1 && (
                          <Label
                            style={{
                              marginRight: 7,
                              display: theme.sizes.isMobile && "block",
                              maxWidth: theme.sizes.isMobile && "fit-content",
                            }}
                            size="tiny"
                            color="teal"
                          >
                            New
                          </Label>
                        )}
                      {util.getChatName(user, thread)}
                    </Table.Cell>
                    <Table.Cell collapsing>
                      {otherUsers.slice(0, 3).map((p) => (
                        <UserChip
                          key={p._id}
                          user={p}
                          compact="very"
                          style={{ marginRight: 5 }}
                        />
                      ))}
                      {otherUsers.length > 3 && (
                        <Label size="tiny">
                          +{otherUsers.length - 3} others
                        </Label>
                      )}
                    </Table.Cell>
                    {!theme.sizes.isMobile && (
                      <Table.Cell>
                        <div
                          style={{
                            display: "-webkit-box",
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            WebkitLineClamp: 2,
                          }}
                        >
                          &quot;{lastMessageSnippet}&quot;
                        </div>
                      </Table.Cell>
                    )}

                    <Table.Cell collapsing>
                      {lastMessageAt &&
                        formatDateRelative(lastMessageAt, { addSuffix: true })}
                    </Table.Cell>
                    <Table.Cell collapsing>
                      <Button
                        basic
                        secondary
                        size="small"
                        content={t("messages.table.view")}
                        onClick={() =>
                          onMessageThreadSelected(thread, theme.sizes.isMobile)
                        }
                      />
                    </Table.Cell>
                  </MessageRow>
                );
              })}
            </Table.Body>
          </Table>
        : messagesLoading ?
          <Loader active />
        : <EmptyBox title={t("messages.empty")} />}
        <NavigationArea>
          <span>{util.pluralise(total, "thread", "total threads")}</span>
          <Pagination
            activePage={page}
            totalPages={Math.ceil(total / 10)}
            onPageChange={(_, { activePage }) =>
              getThreads(activePage as number, query)
            }
          />
        </NavigationArea>
      </Container>
    </>
  );
};

export default MessageCentre;
